// BookStructureCard — embedded on SourceDocumentDetailPage.
//
// Shows the document's BookStructure status with a context-appropriate CTA:
//
//   no structure yet           → "Propose structure" button
//   proposing                  → spinner + "Analyzing — ~60s"
//   proposing_error            → error + "Retry"
//   pending_review + chapters  → summary + "Review & approve" link
//   approved                   → summary + "Trigger ingestion" link
//   rejected                   → muted summary + "Re-propose"
//
// The card itself doesn't open a tree editor — that's the dedicated
// SourceDocumentMapPage at /admin/knowledge/sources/{id}/map.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Spinner } from '@gc-coach/design-system';
import type { BookStructure, SourceDocument } from '@gc-coach/shared';
import { proposeMap } from '../api/endpoints.js';
import { useI18n } from '../i18n/index.js';
import { useBookStructure } from './useBookStructure.js';
import styles from './SourceDocuments.module.css';

export interface BookStructureCardProps {
  doc: SourceDocument;
  /** Called after a successful POST /propose-map so the parent can refresh the SourceDocument. */
  onChange: () => void;
}

export function BookStructureCard({ doc, onChange }: BookStructureCardProps) {
  const navigate = useNavigate();
  const { t, tt } = useI18n();

  const { structure, isLoading, error, refetch } = useBookStructure(doc.book_structure_id);

  const [proposing, setProposing] = useState(false);
  const [proposeError, setProposeError] = useState<string | null>(null);

  async function onPropose() {
    setProposing(true);
    setProposeError(null);
    // If we already have a structure with chapters, this is a user-initiated
    // RE-propose (they clicked the Re-propose button). Force=true bypasses
    // the backend's idempotency guard so a fresh Sonnet pass actually runs.
    const isRepropose =
      structure != null && structure.chapters.length > 0;
    const result = await proposeMap(doc.id, isRepropose ? { force: true } : {});
    setProposing(false);
    if (!result.ok) {
      setProposeError(result.message);
      return;
    }
    // Refresh the SourceDocument so book_structure_id back-links + the
    // useBookStructure hook can pick it up via the new id.
    onChange();
    // Force-poll the freshly-returned structure (in case onChange is
    // debounced/cached upstream).
    void refetch();
  }

  // ─── State 0: doc removed — don't render anything ────────────────
  if (doc.status === 'removed') return null;

  // ─── State 1: no structure yet ────────────────────────────────────
  if (!doc.book_structure_id) {
    return (
      <section className={styles.metaBlock}>
        <header className={styles.metaBlockHeader}>
          <h3>{t('mapping.cardTitle')}</h3>
        </header>
        <p className={styles.cardBody}>{t('mapping.noStructureBody')}</p>
        {proposeError && (
          <Alert severity="danger" onDismiss={() => setProposeError(null)}>
            {proposeError}
          </Alert>
        )}
        <div className={styles.actionFooter}>
          <Button kind="primary" onClick={onPropose} disabled={proposing}>
            {proposing ? <Spinner /> : t('mapping.proposeButton')}
          </Button>
        </div>
      </section>
    );
  }

  // ─── State 2: have an id, structure load failed ───────────────────
  if (error) {
    return (
      <section className={styles.metaBlock}>
        <header className={styles.metaBlockHeader}>
          <h3>{t('mapping.cardTitle')}</h3>
        </header>
        <Alert severity="danger">{error}</Alert>
      </section>
    );
  }

  // ─── State 3: id but structure loading ────────────────────────────
  if (isLoading || !structure) {
    return (
      <section className={styles.metaBlock}>
        <header className={styles.metaBlockHeader}>
          <h3>{t('mapping.cardTitle')}</h3>
        </header>
        <div className={styles.cardLoading}>
          <Spinner />
          <span>{t('mapping.loading')}</span>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.metaBlock}>
      <header className={styles.metaBlockHeader}>
        <h3>{t('mapping.cardTitle')}</h3>
        <span className={statusPillClassForStructure(structure, styles)}>
          {structureStatusText(structure, t)}
        </span>
      </header>

      {/* Proposing: spinner + estimated time */}
      {structure.proposing && (
        <div className={styles.cardLoading}>
          <Spinner />
          <div>
            <strong>{t('mapping.proposingTitle')}</strong>
            <p className={styles.cardSub}>{t('mapping.proposingSub')}</p>
          </div>
        </div>
      )}

      {/* Error: retry button */}
      {structure.proposing_error && !structure.proposing && (
        <>
          <Alert severity="danger">
            <strong>{t('mapping.proposeFailedTitle')}</strong>
            <div className={styles.cardSub}>{structure.proposing_error}</div>
          </Alert>
          {proposeError && (
            <Alert severity="danger" onDismiss={() => setProposeError(null)}>
              {proposeError}
            </Alert>
          )}
          <div className={styles.actionFooter}>
            <Button kind="primary" onClick={onPropose} disabled={proposing}>
              {proposing ? <Spinner /> : t('mapping.retryButton')}
            </Button>
          </div>
        </>
      )}

      {/* Has chapters: summary + review link */}
      {!structure.proposing && !structure.proposing_error && structure.chapters.length > 0 && (
        <>
          <div className={styles.cardSummary}>
            <p>
              {tt('mapping.summaryTemplate', {
                chapters: structure.chapters.length,
                subSections: structure.chapters.reduce(
                  (acc, ch) => acc + ch.sub_sections.length,
                  0,
                ),
              })}
            </p>
            {structure.proposed_by_model && (
              <p className={styles.cardSub}>
                {tt('mapping.proposedByModel', { model: structure.proposed_by_model })}
              </p>
            )}
          </div>
          <div className={styles.actionFooter}>
            <Button
              kind={structure.status === 'pending_review' ? 'primary' : 'ghost'}
              onClick={() => navigate(`/admin/knowledge/sources/${doc.id}/map`)}
            >
              {structure.status === 'pending_review'
                ? t('mapping.reviewButton')
                : t('mapping.viewButton')}
            </Button>
            {(structure.status === 'pending_review' || structure.status === 'rejected') && (
              <Button kind="ghost" onClick={onPropose} disabled={proposing}>
                {proposing ? <Spinner /> : t('mapping.reproposeButton')}
              </Button>
            )}
          </div>
        </>
      )}
    </section>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function structureStatusText(structure: BookStructure, t: (k: string) => string): string {
  if (structure.proposing) return t('mapping.statusProposing');
  if (structure.proposing_error) return t('mapping.statusError');
  if (structure.status === 'approved') return t('mapping.statusApproved');
  if (structure.status === 'rejected') return t('mapping.statusRejected');
  return t('mapping.statusPendingReview');
}

function statusPillClassForStructure(
  structure: BookStructure,
  cls: Record<string, string>,
): string {
  const base = cls['statusPill'] ?? '';
  if (structure.proposing) return `${base} ${cls['statusProcessing'] ?? ''}`;
  if (structure.proposing_error) return `${base} ${cls['statusFailed'] ?? ''}`;
  if (structure.status === 'approved') return `${base} ${cls['statusIngested'] ?? ''}`;
  if (structure.status === 'rejected') return `${base} ${cls['statusRemoved'] ?? ''}`;
  return `${base} ${cls['statusUploaded'] ?? ''}`;
}
